import type { CSSProperties } from 'react'

export const EMPTY_VALUE_LABEL = 'N/A'

export type ColorAccentStyle = {
  className?: string
  style?: CSSProperties
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

const parseHexColor = (color: string): [number, number, number] | null => {
  const hex = color.replace('#', '').trim()
  if (hex.length === 3) {
    const r = parseInt(hex[0]! + hex[0], 16)
    const g = parseInt(hex[1]! + hex[1], 16)
    const b = parseInt(hex[2]! + hex[2], 16)
    return [r, g, b]
  }
  if (hex.length === 6 || hex.length === 8) {
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    if ([r, g, b].some((channel) => Number.isNaN(channel))) return null
    return [r, g, b]
  }
  return null
}

const parseRgbColor = (color: string): [number, number, number] | null => {
  const match = color
    .trim()
    .match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i)
  if (!match) return null
  return [Number(match[1]), Number(match[2]), Number(match[3])].map((channel) =>
    Math.round(clamp01(channel / 255) * 255),
  ) as [number, number, number]
}

const parseHslColor = (color: string): [number, number, number] | null => {
  const match = color
    .trim()
    .match(/^hsla?\(\s*([\d.]+)[,\s]+([\d.]+)%[,\s]+([\d.]+)%/i)
  if (!match) return null

  const h = ((Number(match[1]) % 360) + 360) % 360
  const s = clamp01(Number(match[2]) / 100)
  const l = clamp01(Number(match[3]) / 100)

  const hueToRgb = (p: number, q: number, t: number): number => {
    let value = t
    if (value < 0) value += 1
    if (value > 1) value -= 1
    if (value < 1 / 6) return p + (q - p) * 6 * value
    if (value < 1 / 2) return q
    if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6
    return p
  }

  if (s === 0) {
    const gray = Math.round(l * 255)
    return [gray, gray, gray]
  }

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const r = hueToRgb(p, q, h / 360 + 1 / 3)
  const g = hueToRgb(p, q, h / 360)
  const b = hueToRgb(p, q, h / 360 - 1 / 3)

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)]
}

const parseCssColor = (color: string): [number, number, number] | null => {
  const trimmed = color.trim()
  if (trimmed.startsWith('#')) return parseHexColor(trimmed)
  if (trimmed.startsWith('rgb')) return parseRgbColor(trimmed)
  if (trimmed.startsWith('hsl')) return parseHslColor(trimmed)
  return null
}

const getRelativeLuminance = ([r, g, b]: [number, number, number]): number => {
  const channels = [r, g, b].map((channel) => {
    const value = channel / 255
    return value <= 0.03928
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * channels[0]! + 0.7152 * channels[1]! + 0.0722 * channels[2]!
}

const getReadableTextColor = (color: string): string => {
  const rgb = parseCssColor(color)
  if (!rgb) {
    return 'hsl(var(--foreground))'
  }

  const luminance = getRelativeLuminance(rgb)
  if (luminance > 0.62) {
    return `color-mix(in srgb, ${color} 38%, rgb(15 15 15))`
  }
  if (luminance > 0.35) {
    return `color-mix(in srgb, ${color} 72%, rgb(15 15 15))`
  }
  return `color-mix(in srgb, ${color} 88%, rgb(255 255 255))`
}

const getAccentBackgroundColor = (color: string): string => {
  const rgb = parseCssColor(color)
  const luminance = rgb ? getRelativeLuminance(rgb) : 0.5
  const mixPercent = luminance > 0.62 ? 22 : 16
  return `color-mix(in srgb, ${color} ${mixPercent}%, hsl(var(--card)))`
}

export const getBooleanAccentStyles = (value: boolean): ColorAccentStyle => {
  if (value) {
    const color = 'hsl(142 55% 36%)'
    return {
      className: 'font-medium ring-1',
      style: {
        color: getReadableTextColor(color),
        backgroundColor: getAccentBackgroundColor(color),
        borderColor:
          'color-mix(in srgb, hsl(142 55% 36%) 18%, hsl(var(--border)))',
        boxShadow:
          'inset 0 0 0 1px color-mix(in srgb, hsl(142 55% 36%) 14%, transparent)',
      },
    }
  }

  return {
    className:
      'font-medium ring-1 bg-muted/40 text-muted-foreground ring-border/45',
  }
}
